import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ElementType } from 'react';
import { userNavigationPreferencesService } from '../services/userNavigationPreferencesService';
import { DEFAULT_QUICK_ACTION_IDS, normalizeQuickActionIds } from '../config/quickActions';
import { removeLegacyRawNavigationHistory } from '../utils/navigationHistory';

export interface NavItem {
  id: string;
  name: string;
  icon: ElementType;
  path: string;
  tab?: 'operations' | 'crm' | 'finance' | 'intelligence' | 'admin';
  section?: string;
}

export interface RecentPage {
  id: string;
  name: string;
  path: string;
  permissionPath: string;
  timestamp: number;
  icon?: string;
}

export function getVisibleRecentPages(
  recentPages: readonly RecentPage[],
  recentPagesOwnerId: string | null,
  currentUserId: string | null,
  allowedPaths: ReadonlySet<string>,
): RecentPage[] {
  if (!currentUserId || recentPagesOwnerId !== currentUserId) return [];
  return recentPages.filter((page) => allowedPaths.has(page.permissionPath));
}

export type NavigationPreferencesStatus = 'idle' | 'loading' | 'ready';

const DEFAULT_EXPANDED_SECTIONS = ['bookings', 'ai-tools'] as const;

function createDefaultNavigationPreferences() {
  return {
    favorites: [] as string[],
    expandedSections: new Set<string>(DEFAULT_EXPANDED_SECTIONS),
    pinnedSections: new Set<string>(),
    density: 'normal' as const,
    sectionOrder: {} as Record<string, number>,
    itemUsageCount: {} as Record<string, number>,
    quickActions: [...DEFAULT_QUICK_ACTION_IDS],
  };
}

let navigationPreferencesLoadGeneration = 0;
let navigationPreferencesAbortController: AbortController | null = null;

// Remove the former global raw-query key as soon as the navigation store is
// initialized. This runs even when a user never opens the command palette.
try {
  if (typeof window !== 'undefined') removeLegacyRawNavigationHistory(window.localStorage);
} catch { /* storage unavailable */ }

interface NavigationState {
  navigationPreferencesOwnerId: string | null;
  navigationPreferencesStatus: NavigationPreferencesStatus;
  bindNavigationPreferencesOwner: (userId: string | null) => void;

  // Favorites
  favorites: string[]; // Array of nav item IDs
  addFavorite: (itemId: string) => void;
  removeFavorite: (itemId: string) => void;
  toggleFavorite: (itemId: string) => void;
  isFavorite: (itemId: string) => boolean;

  // Recent pages
  recentPages: RecentPage[];
  recentPagesOwnerId: string | null;
  setRecentPagesOwner: (userId: string | null) => void;
  addRecentPage: (page: RecentPage, userId: string) => void;
  filterRecentPages: (allowedPaths: ReadonlySet<string>) => void;
  clearRecentPages: () => void;

  // Expanded sections
  expandedSections: Set<string>;
  toggleSection: (sectionId: string) => void;
  expandSection: (sectionId: string) => void;
  collapseSection: (sectionId: string) => void;
  expandAll: (sectionIds?: string[]) => void;
  collapseAll: () => void;

  // Sidebar Collapse
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;

  // Pinned sections (always stay expanded)
  pinnedSections: Set<string>;
  togglePinSection: (sectionId: string) => void;
  isPinned: (sectionId: string) => boolean;

  // Active tab
  activeTab: 'operations' | 'crm' | 'finance' | 'intelligence' | 'admin';
  setActiveTab: (tab: 'operations' | 'crm' | 'finance' | 'intelligence' | 'admin') => void;

  // Visual density
  density: 'compact' | 'normal' | 'comfortable';
  setDensity: (density: 'compact' | 'normal' | 'comfortable') => void;

  // Section order (custom ordering)
  sectionOrder: Record<string, number>;
  setSectionOrder: (order: Record<string, number>) => void;

  // Most used tracking
  itemUsageCount: Record<string, number>;
  incrementUsage: (itemId: string) => void;
  getMostUsed: (limit?: number) => string[];

  // Quick Actions (user-customizable)
  quickActions: string[];
  setQuickActions: (actions: string[], userId?: string) => void;
  addQuickAction: (actionId: string, userId?: string) => void;
  removeQuickAction: (actionId: string, userId?: string) => void;

  // Database sync
  loadFromDatabase: (userId: string, signal?: AbortSignal) => Promise<void>;
  syncToDatabase: (userId: string) => void;

  // Search filter
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export const useNavigationStore = create<NavigationState>()(
  persist(
    (set, get) => ({
      navigationPreferencesOwnerId: null,
      navigationPreferencesStatus: 'idle',
      bindNavigationPreferencesOwner: (userId) => {
        const previousOwnerId = get().navigationPreferencesOwnerId;
        if (previousOwnerId === userId) return;
        if (previousOwnerId) {
          userNavigationPreferencesService.cancelPendingSync(previousOwnerId);
        }
        navigationPreferencesLoadGeneration += 1;
        navigationPreferencesAbortController?.abort();
        navigationPreferencesAbortController = null;

        set({
          ...createDefaultNavigationPreferences(),
          navigationPreferencesOwnerId: userId,
          navigationPreferencesStatus: userId ? 'loading' : 'idle',
        });
      },

      // Favorites
      favorites: [],
      addFavorite: (itemId) =>
        set((state) => state.navigationPreferencesOwnerId ? ({
          favorites: [...state.favorites, itemId],
        }) : {}),
      removeFavorite: (itemId) =>
        set((state) => state.navigationPreferencesOwnerId ? ({
          favorites: state.favorites.filter((id) => id !== itemId),
        }) : {}),
      toggleFavorite: (itemId) => {
        const { favorites } = get();
        if (favorites.includes(itemId)) {
          get().removeFavorite(itemId);
        } else {
          get().addFavorite(itemId);
        }
      },
      isFavorite: (itemId) => get().favorites.includes(itemId),

      // Recent pages
      recentPages: [],
      recentPagesOwnerId: null,
      setRecentPagesOwner: (userId) => set((state) => (
        state.recentPagesOwnerId === userId
          ? {}
          : { recentPagesOwnerId: userId, recentPages: [] }
      )),
      addRecentPage: (page, userId) =>
        set((state) => {
          if (!userId || state.recentPagesOwnerId !== userId) return {};
          const filtered = state.recentPages.filter((p) => p.path !== page.path);
          const updated = [page, ...filtered].slice(0, 8);
          return { recentPages: updated };
        }),
      filterRecentPages: (allowedPaths) => set((state) => ({
        recentPages: state.recentPages.filter((page) => allowedPaths.has(page.permissionPath)),
      })),
      clearRecentPages: () => set({ recentPages: [] }),

      // Expanded sections
      expandedSections: new Set(DEFAULT_EXPANDED_SECTIONS),
      toggleSection: (sectionId) =>
        set((state) => {
          if (!state.navigationPreferencesOwnerId) return {};
          const newExpanded = new Set(state.expandedSections);
          if (newExpanded.has(sectionId)) {
            newExpanded.delete(sectionId);
          } else {
            newExpanded.add(sectionId);
          }
          return { expandedSections: newExpanded };
        }),
      expandSection: (sectionId) =>
        set((state) => (
          // Already open (or no account bound): return the same state, so zustand skips the update entirely and
          // nothing re-renders. Returning {} would still create a new state object and notify every subscriber.
          state.navigationPreferencesOwnerId && !state.expandedSections.has(sectionId)
            ? { expandedSections: new Set(state.expandedSections).add(sectionId) }
            : state
        )),
      collapseSection: (sectionId) =>
        set((state) => {
          if (!state.navigationPreferencesOwnerId) return {};
          const newExpanded = new Set(state.expandedSections);
          newExpanded.delete(sectionId);
          return { expandedSections: newExpanded };
        }),
      expandAll: (sectionIds) => set((state) => state.navigationPreferencesOwnerId ? ({
        expandedSections: sectionIds
          ? new Set(sectionIds)
          : new Set(state.expandedSections),
      }) : {}),
      collapseAll: () => set((state) => state.navigationPreferencesOwnerId
        ? { expandedSections: new Set() }
        : {}),

      // Sidebar Collapse
      isSidebarCollapsed: false,
      toggleSidebar: () =>
        set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),

      // Pinned sections
      pinnedSections: new Set(),
      togglePinSection: (sectionId) =>
        set((state) => {
          if (!state.navigationPreferencesOwnerId) return {};
          const newPinned = new Set(state.pinnedSections);
          if (newPinned.has(sectionId)) {
            newPinned.delete(sectionId);
          } else {
            newPinned.add(sectionId);
          }
          return { pinnedSections: newPinned };
        }),
      isPinned: (sectionId) => get().pinnedSections.has(sectionId),

      // Active tab
      activeTab: 'operations',
      setActiveTab: (tab) => set({ activeTab: tab }),

      // Visual density
      density: 'normal',
      setDensity: (density) => set((state) => state.navigationPreferencesOwnerId ? { density } : {}),

      // Section order
      sectionOrder: {},
      setSectionOrder: (order) => set((state) => state.navigationPreferencesOwnerId ? { sectionOrder: order } : {}),

      // Most used tracking
      itemUsageCount: {},
      incrementUsage: (itemId) =>
        set((state) => state.navigationPreferencesOwnerId ? ({
          itemUsageCount: {
            ...state.itemUsageCount,
            [itemId]: (state.itemUsageCount[itemId] || 0) + 1,
          },
        }) : {}),
      getMostUsed: (limit = 5) => {
        const { itemUsageCount } = get();
        return Object.entries(itemUsageCount)
          .sort(([, a], [, b]) => b - a)
          .slice(0, limit)
          .map(([id]) => id);
      },

      // Quick Actions
      quickActions: DEFAULT_QUICK_ACTION_IDS,
      setQuickActions: (actions, userId) => {
        const ownerId = get().navigationPreferencesOwnerId;
        if (!ownerId || (userId && userId !== ownerId)) return;
        const normalizedActions = normalizeQuickActionIds(actions, 'setQuickActions');
        set({ quickActions: normalizedActions });
        userNavigationPreferencesService.debouncedSync(ownerId, { quickActions: normalizedActions });
      },
      addQuickAction: (actionId, userId) => {
        const ownerId = get().navigationPreferencesOwnerId;
        if (!ownerId || (userId && userId !== ownerId)) return;
        const normalizedAction = normalizeQuickActionIds([actionId], 'addQuickAction')[0];
        if (!normalizedAction) {
          return;
        }

        set((state) => {
          const newQuickActions = normalizeQuickActionIds([...state.quickActions, normalizedAction], 'addQuickAction');
          userNavigationPreferencesService.debouncedSync(ownerId, { quickActions: newQuickActions });
          return { quickActions: newQuickActions };
        });
      },
      removeQuickAction: (actionId, userId) => {
        const ownerId = get().navigationPreferencesOwnerId;
        if (!ownerId || (userId && userId !== ownerId)) return;
        const normalizedAction = normalizeQuickActionIds([actionId], 'removeQuickAction')[0];
        if (!normalizedAction) {
          return;
        }

        set((state) => {
          const newQuickActions = state.quickActions.filter((id) => id !== normalizedAction);
          userNavigationPreferencesService.debouncedSync(ownerId, { quickActions: newQuickActions });
          return { quickActions: newQuickActions };
        });
      },

      // Database sync
      loadFromDatabase: async (userId: string, externalSignal?: AbortSignal) => {
        if (!userId) return;
        if (get().navigationPreferencesOwnerId !== userId) {
          get().bindNavigationPreferencesOwner(userId);
        }

        // Open/closed sections already held in this browser for this account (restored from localStorage, or loaded
        // earlier in this visit) are the person's latest choice: keep them. Re-loading used to replace them with the
        // saved copy on every page change, so the sidebar closed after each click. A fresh account bind ('loading')
        // still takes the saved copy.
        const keepLocalExpandedSections = get().navigationPreferencesOwnerId === userId
          && get().navigationPreferencesStatus !== 'loading';

        navigationPreferencesAbortController?.abort();
        const controller = new AbortController();
        navigationPreferencesAbortController = controller;
        const loadGeneration = ++navigationPreferencesLoadGeneration;
        const abortFromExternalSignal = () => controller.abort();
        externalSignal?.addEventListener('abort', abortFromExternalSignal, { once: true });
        if (externalSignal?.aborted) controller.abort();

        set((state) => state.navigationPreferencesOwnerId === userId
          ? { navigationPreferencesStatus: 'loading' }
          : {});

        try {
          const prefs = await userNavigationPreferencesService.fetchPreferences(userId, controller.signal);
          if (
            controller.signal.aborted ||
            loadGeneration !== navigationPreferencesLoadGeneration ||
            get().navigationPreferencesOwnerId !== userId
          ) {
            return;
          }

          const nextPreferences = prefs ? {
            favorites: Array.isArray(prefs.favorites) ? prefs.favorites : [],
            sectionOrder: prefs.sectionOrder || {},
            expandedSections: new Set(Array.isArray(prefs.expandedSections) ? prefs.expandedSections : []),
            pinnedSections: new Set(Array.isArray(prefs.pinnedSections) ? prefs.pinnedSections : []),
            density: prefs.density || 'normal',
            // Route alignment owns activeTab; never restore a stale saved domain here.
            itemUsageCount: prefs.itemUsageCount || {},
            quickActions: Array.isArray(prefs.quickActions)
              ? normalizeQuickActionIds(prefs.quickActions, 'user preferences')
              : [...DEFAULT_QUICK_ACTION_IDS],
          } : createDefaultNavigationPreferences();

          set({
            ...nextPreferences,
            ...(keepLocalExpandedSections ? { expandedSections: get().expandedSections } : {}),
            navigationPreferencesOwnerId: userId,
            navigationPreferencesStatus: 'ready',
          });
        } finally {
          externalSignal?.removeEventListener('abort', abortFromExternalSignal);
          if (navigationPreferencesAbortController === controller) {
            navigationPreferencesAbortController = null;
          }
        }
      },
      syncToDatabase: (userId: string) => {
        const state = get();
        if (!userId || state.navigationPreferencesOwnerId !== userId) return;
        userNavigationPreferencesService.debouncedSync(userId, {
          favorites: state.favorites,
          sectionOrder: state.sectionOrder,
          expandedSections: Array.from(state.expandedSections),
          pinnedSections: Array.from(state.pinnedSections),
          density: state.density,
          itemUsageCount: state.itemUsageCount,
          quickActions: state.quickActions,
        });
      },

      // Search filter
      searchQuery: '',
      setSearchQuery: (query) => set({ searchQuery: query }),
    }),
    {
      name: 'navigation-store',
      partialize: (state) => ({
        navigationPreferencesOwnerId: state.navigationPreferencesOwnerId,
        favorites: state.favorites,
        recentPages: state.recentPages,
        recentPagesOwnerId: state.recentPagesOwnerId,
        expandedSections: Array.from(state.expandedSections),
        pinnedSections: Array.from(state.pinnedSections),
        density: state.density,
        sectionOrder: state.sectionOrder,
        itemUsageCount: state.itemUsageCount,
        quickActions: state.quickActions,
        isSidebarCollapsed: state.isSidebarCollapsed,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Convert arrays back to Sets
          state.expandedSections = new Set(state.expandedSections as unknown as string[]);
          state.pinnedSections = new Set(state.pinnedSections as unknown as string[]);
          state.navigationPreferencesStatus = 'idle';
        }
      },
    }
  )
);



























